import { create } from 'zustand';

interface SuccessMessages {
  signUp: (name: string) => string;
  signIn: (name: string) => string;
}

interface AuthBottomSheetState {
  isSheetOpen: boolean;
  onSuccessCallback: ((isSignUp: boolean, name: string) => void) | null;
  successMessages: SuccessMessages;
  openSheet: (
    callback: (isSignUp: boolean, name: string) => void,
    messages?: Partial<SuccessMessages>
  ) => void;
  closeSheet: () => void;
  triggerSuccess: (isSignUp: boolean, name: string) => void;
}

const useAuthBottomSheetStore = create<AuthBottomSheetState>((set, get) => ({
  isSheetOpen: false,
  onSuccessCallback: null,
  successMessages: {
    signUp: (name) => `Hi ${name}! Welcome.`,
    signIn: (name) => `Welcome back, ${name}!`,
  },

  // Action to open the sheet from any component
  openSheet: (callback, messages) => {
    set((state) => ({
      isSheetOpen: true,
      onSuccessCallback: callback,
      // Merge default messages with any custom ones provided for this action
      successMessages: { ...state.successMessages, ...messages },
    }));
  },

  // Action to close the sheet
  closeSheet: () => {
    set({ isSheetOpen: false, onSuccessCallback: null });
  },

  // Action called by SignUpPage on success
  triggerSuccess: (isSignUp, name) => {
    const { onSuccessCallback, successMessages } = get();

    // 1. Close the bottom sheet first
    set({ isSheetOpen: false });

    // 2. Run the original action that was saved
    if (onSuccessCallback) {
      onSuccessCallback(isSignUp, name);
    }

    // 3. Show toast notification using your existing toast system
    setTimeout(() => {
      const message = isSignUp
        ? successMessages.signUp(name)
        : successMessages.signIn(name);

      // Import the toast store dynamically to avoid circular dependencies
      import('@/stores/ToastStore').then(({ default: useToastStore }) => {
        useToastStore.getState().showToast(message);
      });

      // Clear the callback
      set({ onSuccessCallback: null });
    }, 100); // Small delay to ensure proper rendering
  },
}));

export default useAuthBottomSheetStore;
